import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Paperclip, Search } from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { usePatientMessageInbox } from '@/hooks/usePatientMessages';
import { useIsDesktopNav } from '@/hooks/useNavBreakpoint';
import { InboxConversationPanel } from '@/components/messages/InboxConversationPanel';
import { NewMessageSlideOver } from '@/components/messages/NewMessageSlideOver';
import {
  Banner,
  Button,
  Card,
  EmptyState,
  Input,
  SegmentedControl,
  Select,
  Skeleton,
  SkeletonText,
} from '@/components/ui';
import { Avatar } from '@/components/ui/Avatar';
import { TYPE, TYPE_TABULAR } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { conversationPreview, formatInboxListWhen, inboxLocale } from '@/utils/messageInboxFormat';
import {
  filterMessageConversations,
  patientDisplayName,
  unreadConversationCount,
  type MessageInboxTab,
  type PatientMessageConversation,
} from '@/utils/patientMessagesPath';

export function MessagesInboxPanel({
  onMobileConversationChange,
  composeOpen,
  onComposeOpenChange,
  onDesktopThreadSelectedChange,
}: {
  onMobileConversationChange?: (open: boolean) => void;
  composeOpen: boolean;
  onComposeOpenChange: (open: boolean) => void;
  onDesktopThreadSelectedChange?: (selected: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { hasPermission } = useAuth();
  const isDesktop = useIsDesktopNav();
  const inbox = usePatientMessageInbox(activeClinicId);
  const locale = inboxLocale(i18n.language);

  const [tab, setTab] = useState<MessageInboxTab>('unread');
  const [search, setSearch] = useState('');
  const [patientId, setPatientId] = useState<string | null>(null);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);

  const conversations = useMemo(() => inbox.data ?? [], [inbox.data]);
  const unreadCount = unreadConversationCount(conversations);
  const canCreate = Boolean(activeClinicId && hasPermission(activeClinicId, 'messages', 'create'));

  const patients = useMemo(() => {
    const seen = new Map<string, string>();
    for (const item of conversations) {
      if (!seen.has(item.patient_id)) {
        seen.set(item.patient_id, patientDisplayName(item));
      }
    }
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1], i18n.language));
  }, [conversations, i18n.language]);

  const visible = useMemo(
    () =>
      filterMessageConversations(conversations, {
        tab,
        search,
        patientId,
      }),
    [conversations, patientId, search, tab],
  );

  useEffect(() => {
    if (patientId && selectedPatientId && selectedPatientId !== patientId) {
      setSelectedPatientId(isDesktop ? (visible[0]?.patient_id ?? null) : null);
      return;
    }
    if (selectedPatientId && conversations.some((item) => item.patient_id === selectedPatientId)) {
      return;
    }
    if (isDesktop && visible[0]) {
      setSelectedPatientId(visible[0].patient_id);
      return;
    }
    setSelectedPatientId(null);
  }, [conversations, isDesktop, patientId, selectedPatientId, visible]);

  const selected = conversations.find((item) => item.patient_id === selectedPatientId) ?? null;
  const mobileConversation = !isDesktop && selected != null;
  const desktopThreadSelected = isDesktop && selected != null;

  useLayoutEffect(() => {
    onMobileConversationChange?.(mobileConversation);
  }, [mobileConversation, onMobileConversationChange]);

  useLayoutEffect(() => {
    onDesktopThreadSelectedChange?.(desktopThreadSelected);
  }, [desktopThreadSelected, onDesktopThreadSelectedChange]);

  useLayoutEffect(() => {
    return () => onDesktopThreadSelectedChange?.(false);
  }, [onDesktopThreadSelectedChange]);

  if (!activeClinicId) {
    return (
      <Banner tone="error">{t('messages.inbox.no_clinic', 'You have no active clinic.')}</Banner>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <NewMessageSlideOver
        open={composeOpen}
        clinicId={activeClinicId}
        onClose={() => onComposeOpenChange(false)}
        allowChangePatient
      />

      <div
        className={cn(
          'grid min-h-0 min-w-0 flex-1 gap-s5',
          mobileConversation
            ? 'grid-cols-1'
            : 'grid-cols-1 nav:grid-cols-[minmax(0,380px)_minmax(0,1fr)]',
        )}
      >
        <Card
          className={cn(
            'flex min-h-0 min-w-0 flex-col overflow-hidden p-s4',
            mobileConversation && 'hidden',
          )}
        >
          <div className="flex min-w-0 flex-wrap items-center gap-s3">
            <SegmentedControl
              ariaLabel={t('messages.inbox.view', 'Visning')}
              value={tab}
              onChange={setTab}
              options={[
                {
                  value: 'unread',
                  label:
                    unreadCount > 0
                      ? `${t('messages.inbox.unread', 'Ulæste')} ${unreadCount}`
                      : t('messages.inbox.unread', 'Ulæste'),
                },
                { value: 'all', label: t('messages.inbox.all', 'Alle') },
              ]}
            />
            <Select
              value={patientId ?? ''}
              onChange={(e) => setPatientId(e.target.value || null)}
              aria-label={t('messages.inbox.all_patients', 'Alle patienter')}
              className="min-w-0 flex-1"
            >
              <option value="">{t('messages.inbox.all_patients', 'Alle patienter')}</option>
              {patients.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </Select>
          </div>
          <div className="mt-s3 min-w-0">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('messages.inbox.search_placeholder', 'Søg i beskeder...')}
              leadingIcon={<Search className="h-4 w-4" />}
              aria-label={t('messages.inbox.search_placeholder', 'Søg i beskeder...')}
            />
          </div>

          <div className="mt-s3 min-h-0 flex-1 overflow-y-auto">
            {inbox.error ? (
              <Banner
                tone="error"
                title={t('messages.error_loading')}
                action={
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => void inbox.refetch()}
                  >
                    {t('app.retry')}
                  </Button>
                }
              />
            ) : inbox.isLoading ? (
              <ThreadListSkeleton />
            ) : visible.length === 0 ? (
              <EmptyState
                icon={<MessageSquare className="h-7 w-7" />}
                title={
                  search || patientId
                    ? t('messages.inbox.empty_search', 'Ingen matchende beskeder')
                    : tab === 'unread'
                      ? t('messages.inbox.empty_unread', 'Ingen ulæste beskeder')
                      : t('messages.inbox.empty_all', 'Ingen patientbeskeder endnu')
                }
                description={
                  search || patientId
                    ? undefined
                    : tab === 'unread'
                      ? t(
                          'messages.inbox.empty_unread_body',
                          'Når en patient skriver, vises samtalen her.',
                        )
                      : t(
                          'messages.inbox.empty_all_body',
                          'Send den første besked, så vises den i patientens portal.',
                        )
                }
              />
            ) : (
              <ul className="flex flex-col gap-s1">
                {visible.map((item) => (
                  <ThreadRow
                    key={item.patient_id}
                    item={item}
                    locale={locale}
                    youPrefix={t('messages.inbox.you_prefix', 'Du: ')}
                    selected={item.patient_id === selectedPatientId}
                    onSelect={() => setSelectedPatientId(item.patient_id)}
                  />
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card
          className={cn(
            'flex min-h-0 min-w-0 flex-col overflow-hidden p-0',
            !mobileConversation && 'hidden nav:flex',
            mobileConversation && 'flex',
          )}
        >
          <InboxConversationPanel
            conversation={selected}
            clinicId={activeClinicId}
            canReply={canCreate}
            showBack={!isDesktop}
            onBack={() => setSelectedPatientId(null)}
          />
        </Card>
      </div>
    </div>
  );
}

function ThreadRow({
  item,
  locale,
  youPrefix,
  selected,
  onSelect,
}: {
  item: PatientMessageConversation;
  locale: ReturnType<typeof inboxLocale>;
  youPrefix: string;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const unread = item.unread_count > 0;
  const name = patientDisplayName(item);

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected ? 'true' : undefined}
        className={cn(
          'flex w-full min-w-0 items-start gap-s3 rounded-pop p-s3 text-left',
          selected
            ? 'border border-ds-teal-line bg-ds-teal-tint'
            : 'border border-transparent hover:bg-ds-neutral-tint',
        )}
      >
        <Avatar name={name} size={40} />
        <span className="min-w-0 flex-1">
          <span className="flex items-start gap-s2">
            <span
              className={cn(TYPE.control, 'min-w-0 flex-1 truncate font-semibold text-ds-text')}
            >
              {name}
            </span>
            <span className={cn(TYPE.meta, TYPE_TABULAR, 'shrink-0 font-medium text-ds-muted')}>
              {formatInboxListWhen(item.latest_created_at, locale)}
            </span>
          </span>
          <span className="mt-s1 flex items-start gap-s2">
            <span className={cn(TYPE.small, 'min-w-0 flex-1 text-ds-muted')}>
              {conversationPreview(item.latest_content, item.latest_sender, youPrefix)}
            </span>
            {item.latest_has_attachment ? (
              <Paperclip
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ds-muted"
                aria-label={t('messages.inbox.attachment', 'Vedhæftning')}
              />
            ) : null}
          </span>
        </span>
        {unread ? (
          <span
            className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-ds-coral-badge"
            aria-label={t('messages.inbox.unread_indicator', 'Ulæst')}
          />
        ) : null}
      </button>
    </li>
  );
}

function ThreadListSkeleton() {
  return (
    <ul className="flex flex-col gap-s1">
      {Array.from({ length: 5 }, (_, i) => (
        <li key={i} className="flex items-start gap-s3 p-s3">
          <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1">
            <Skeleton className="h-4 w-1/2" />
            <SkeletonText lines={2} className="mt-s2" />
          </div>
        </li>
      ))}
    </ul>
  );
}
